import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  BoardShell,
  ChartTooltip,
  DataTable,
  KpiGrid,
  LEGEND,
  NoteList,
  Panel,
  Section,
  fmtMoneyM,
  fmtPct,
  signedPct,
  type DataColumn,
  type KpiItem,
} from '@/components/qxo/qxoBoardPrimitives';
import {
  BEACON_LIKE_FOR_LIKE,
  BEACON_QUARTERS,
  FILINGS_DESK_AS_OF,
  FILINGS_SOURCES,
  MANAGEMENT_PROOF_POINTS,
  PLATFORM_BASELINE,
  PREFERRED_CASH_DIVIDENDS,
  PRODUCT_LINES,
  PRODUCT_LINE_YOY,
  QXO_QUARTERS,
  WORKING_CAPITAL,
  type LikeForLikeRow,
  type ProductLineRow,
  type QuarterRow,
} from '@/components/qxo/qxoFilingsData';

const BEACON_FY24 = BEACON_QUARTERS.find((q) => q.period === 'FY 24')!;
const BEACON_FY25 = PLATFORM_BASELINE.find((p) => p.platform.startsWith('Beacon'))!;
const LATEST = QXO_QUARTERS[QXO_QUARTERS.length - 1];

/** One timeline: Beacon's four 2024 quarters, then QXO's quarters with Beacon inside. */
const MARGIN_TAPE = [
  ...BEACON_QUARTERS.filter((q) => q.period.startsWith('Q')).map((q) => ({
    period: q.period,
    beaconMargin: q.adjEbitdaMarginPct,
    qxoMargin: null as number | null,
    sales: q.netSales,
  })),
  { period: 'Q1–Apr 25', beaconMargin: 5.0, qxoMargin: null, sales: 2694.6 },
  ...QXO_QUARTERS.filter((q) => q.period !== 'Q2 25').map((q) => ({
    period: q.period,
    beaconMargin: null as number | null,
    qxoMargin: q.adjEbitdaMarginPct,
    sales: q.netSales,
  })),
];

const FCF_VS_PREF = QXO_QUARTERS.filter((q) => q.cfoM != null && q.period !== 'Q2 25').map((q) => {
  const pref = PREFERRED_CASH_DIVIDENDS.find((p) => p.period === q.period);
  return {
    period: q.period,
    fcf: q.cfoM! - (q.capexM ?? 0),
    preferred: pref ? -pref.paid : null,
  };
});

const TTM_FCF = FCF_VS_PREF.reduce((s, r) => s + r.fcf, 0);
const TTM_PREF = PREFERRED_CASH_DIVIDENDS.reduce((s, r) => s + r.paid, 0);

const WC_ROWS = WORKING_CAPITAL.map((row) => ({
  ...row,
  netTradeWc: row.receivables + row.inventory + row.rebates - row.payables,
  rebatesToInventoryPct: (row.rebates / row.inventory) * 100,
}));

const KPIS: KpiItem[] = [
  {
    id: 'beacon-fy25',
    label: 'Beacon perimeter, FY25',
    value: `${fmtMoneyM(BEACON_FY25.fy25AdjEbitda)} · ${fmtPct(BEACON_FY25.fy25MarginPct)}`,
    hint: `Adj. EBITDA vs ${fmtMoneyM(BEACON_FY24.adjEbitda)} (${fmtPct(BEACON_FY24.adjEbitdaMarginPct)}) in Beacon\u2019s last year alone. Down ${fmtPct(Math.abs((BEACON_FY25.fy25AdjEbitda / BEACON_FY24.adjEbitda! - 1) * 100), 0)}.`,
    tone: 'danger',
  },
  {
    id: 'lfl',
    label: 'Same-branch sales, last 4 qtrs',
    value: BEACON_LIKE_FOR_LIKE.map((r) => signedPct(r.salesYoyPct, 0)).join(' / '),
    hint: 'Beacon perimeter vs the same quarter a year earlier, ex-software and ex-Kodiak. Q3 25 → Q2 26.',
    tone: 'warning',
  },
  {
    id: 'gm',
    label: 'Adjusted gross margin',
    value: fmtPct(LATEST.grossMarginPct),
    hint: `Q2 26. Beacon printed ${fmtPct(BEACON_FY24.grossMarginPct)} for FY24 and 26.3% in Q3 24. Private label and pricing tools have not shown up yet.`,
    tone: 'danger',
  },
  {
    id: 'sga',
    label: 'SG&A / net sales',
    value: fmtPct((LATEST.sgaM! / LATEST.netSales) * 100),
    hint: 'Q2 26, including Kodiak. Q1 26 was 28.7%. Beacon ran ~18% adjusted opex.',
    tone: 'warning',
  },
  {
    id: 'fcf',
    label: 'FCF vs preferred, TTM',
    value: `${fmtMoneyM(TTM_FCF)} vs ${fmtMoneyM(TTM_PREF)}`,
    hint: 'Q3 25 – Q2 26 operating cash flow less capex, against preferred dividends paid in cash. Nothing left for common yet.',
    tone: 'danger',
  },
  {
    id: 'target',
    label: 'Margin the 2030 plan needs',
    value: `${fmtPct((BEACON_FY25.target2030EbitdaB * 1000) / BEACON_FY25.fy25Sales * 100, 0)} on flat sales`,
    hint: `$2.0B Beacon-platform EBITDA on FY25 sales of ${fmtMoneyM(BEACON_FY25.fy25Sales)}; ~14% if sales reach $14B. Best Beacon quarter ever: 11.7%.`,
    tone: 'warning',
  },
];

const QUARTER_COLUMNS: DataColumn<QuarterRow>[] = [
  { key: 'period', header: 'Quarter', render: (r) => r.period },
  { key: 'who', header: 'Filed by', render: (r) => r.perimeter.replace(' standalone', '').replace(' reported', '') },
  { key: 'sales', header: 'Net sales', align: 'right', render: (r) => fmtMoneyM(r.netSales) },
  { key: 'gm', header: 'Adj. GM', align: 'right', render: (r) => fmtPct(r.grossMarginPct) },
  { key: 'sga', header: 'SG&A', align: 'right', render: (r) => fmtMoneyM(r.sgaM) },
  { key: 'ebitda', header: 'Adj. EBITDA', align: 'right', render: (r) => fmtMoneyM(r.adjEbitda) },
  {
    key: 'margin',
    header: 'Margin',
    align: 'right',
    render: (r) => fmtPct(r.adjEbitdaMarginPct),
    tone: (r) => (r.adjEbitdaMarginPct != null && r.adjEbitdaMarginPct < 7 ? 'text-rose-300' : undefined),
  },
  { key: 'eps', header: 'Adj. EPS', align: 'right', render: (r) => (r.adjDilutedEps == null ? '—' : `$${r.adjDilutedEps.toFixed(2)}`) },
  { key: 'cfo', header: 'CFO', align: 'right', render: (r) => fmtMoneyM(r.cfoM) },
];

const LFL_COLUMNS: DataColumn<LikeForLikeRow>[] = [
  { key: 'period', header: 'Compare', render: (r) => r.period },
  { key: 'ya', header: 'Beacon, year ago', align: 'right', render: (r) => fmtMoneyM(r.beaconYearAgoSales) },
  { key: 'now', header: 'Same branches under QXO', align: 'right', render: (r) => fmtMoneyM(r.qxoPerimeterSales) },
  {
    key: 'yoy',
    header: 'Sales YoY',
    align: 'right',
    render: (r) => signedPct(r.salesYoyPct),
    tone: (r) => (r.salesYoyPct < -5 ? 'text-rose-300' : r.salesYoyPct < 0 ? 'text-amber-300' : 'text-emerald-300'),
  },
  { key: 'm0', header: 'Margin then', align: 'right', render: (r) => fmtPct(r.beaconYearAgoMarginPct) },
  { key: 'm1', header: 'Margin now', align: 'right', render: (r) => fmtPct(r.qxoPerimeterMarginPct) },
  {
    key: 'eb',
    header: 'EBITDA YoY',
    align: 'right',
    render: (r) => (r.ebitdaYoyPct == null ? '—' : signedPct(r.ebitdaYoyPct)),
    tone: (r) => (r.ebitdaYoyPct != null && r.ebitdaYoyPct < 0 ? 'text-rose-300' : undefined),
  },
];

const PRODUCT_COLUMNS: DataColumn<ProductLineRow>[] = [
  { key: 'period', header: 'Quarter', render: (r) => r.period },
  { key: 'who', header: 'Filed by', render: (r) => (r.perimeter === 'QXO reported' ? 'QXO' : 'Beacon') },
  { key: 'resi', header: 'Residential', align: 'right', render: (r) => fmtMoneyM(r.residential) },
  { key: 'nonresi', header: 'Non-residential', align: 'right', render: (r) => fmtMoneyM(r.nonResidential) },
  { key: 'compl', header: 'Complementary', align: 'right', render: (r) => fmtMoneyM(r.complementary) },
  { key: 'total', header: 'Total', align: 'right', render: (r) => fmtMoneyM(r.residential + r.nonResidential + r.complementary) },
  { key: 'resiMix', header: 'Resi mix', align: 'right', render: (r) => fmtPct((r.residential / (r.residential + r.nonResidential + r.complementary)) * 100, 0) },
];

const WC_COLUMNS: DataColumn<(typeof WC_ROWS)[number]>[] = [
  { key: 'date', header: 'Balance sheet', render: (r) => r.date },
  { key: 'ar', header: 'Receivables', align: 'right', render: (r) => fmtMoneyM(r.receivables) },
  { key: 'inv', header: 'Inventory', align: 'right', render: (r) => fmtMoneyM(r.inventory) },
  { key: 'reb', header: 'Vendor rebates due', align: 'right', render: (r) => fmtMoneyM(r.rebates) },
  { key: 'ap', header: 'Payables', align: 'right', render: (r) => fmtMoneyM(r.payables) },
  { key: 'net', header: 'Net trade WC', align: 'right', render: (r) => fmtMoneyM(r.netTradeWc) },
  { key: 'ratio', header: 'Rebates / inventory', align: 'right', render: (r) => fmtPct(r.rebatesToInventoryPct, 0) },
  { key: 'cash', header: 'Cash', align: 'right', render: (r) => fmtMoneyM(r.cash) },
];

const STATUS_BADGE: Record<string, string> = {
  pending: 'not yet testable',
  'on track': 'on track',
  missed: 'missed so far',
  partial: 'mixed',
};

export function QxoFilingsDesk() {
  return (
    <BoardShell
      id="filings"
      title="QXO"
      subtitle="What the filings say about the Beacon platform"
      blurb="Everything on this board is a filed number or a like-for-like derived from filed numbers. The question it answers: has the Beacon business gotten better or worse since Jacobs bought it, and what would it take to hit the 2030 targets."
      asOf={FILINGS_DESK_AS_OF}
      sourceLine="QXO and Beacon 10-Q / 10-K / 8-K on EDGAR"
      footer="Sources: QXO 10-Q Q2 26, 10-K FY25, 8-Ks of 1 Jul and 9 Jul 2026, quarterly earnings releases; Beacon Roofing Supply FY2024 earnings 8-Ks and the predecessor stub in QXO's 8-K/A. Same-perimeter rows strip legacy QXO software (~$14–15M/qtr) and Kodiak. Details in docs/QXO_FILINGS_STREET_DESK.md."
    >
      <KpiGrid items={KPIS} />

      <Section title="Operating record">
        Beacon printed 9.5% EBITDA margins as an independent company. Inside QXO the same branches did 8.2% in
        FY25 and 0.1% in the Q1 26 trough. Revenue on those branches has fallen year over year in each of the
        last four quarters.
      </Section>

      <div className="grid min-w-0 grid-cols-1 gap-3 px-4 pb-4 lg:grid-cols-2">
        <Panel datasetId="beacon-margins"
          tall
          title="Beacon margins: before and after the takeover"
          unit="Adjusted EBITDA margin, %, with net sales as bars"
          takeaway="Gold is Beacon reporting for itself; pink is QXO reporting the same branches. The seasonal shape is the same; the level dropped roughly 2–3 points in the second half and collapsed in Q1 26."
          note="Q2 25 is omitted (QXO only owned Beacon for 63 days). 'Q1–Apr 25' is the four-month predecessor stub. Q2 26 pink bar includes $595M Kodiak; margin is consolidated."
        >
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={MARGIN_TAPE} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#1e4976" vertical={false} />
              <XAxis dataKey="period" tick={{ fill: '#93c5fd', fontSize: 10 }} interval={0} axisLine={false} tickLine={false} />
              <YAxis yAxisId="sales" tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v: number) => `${(v / 1000).toFixed(1)}B`} />
              <YAxis yAxisId="m" orientation="right" tick={{ fill: '#fbbf24', fontSize: 10 }} axisLine={false} tickLine={false} unit="%" domain={[0, 14]} />
              <Tooltip content={<ChartTooltip formatValue={(v, name) => (name.includes('sales') ? fmtMoneyM(v) : fmtPct(v))} />} />
              <Bar yAxisId="sales" dataKey="sales" name="Net sales" fill="#1d4ed8" radius={[3, 3, 0, 0]} />
              <Line yAxisId="m" type="monotone" dataKey="beaconMargin" name="Beacon margin (Beacon filed)" stroke="#fbbf24" strokeWidth={2} dot={{ r: 4, fill: '#fbbf24' }} connectNulls={false} />
              <Line yAxisId="m" type="monotone" dataKey="qxoMargin" name="Same branches (QXO filed)" stroke="#fb7185" strokeWidth={2} dot={{ r: 4, fill: '#fb7185' }} connectNulls={false} />
              <Legend wrapperStyle={LEGEND} />
            </ComposedChart>
          </ResponsiveContainer>
        </Panel>

        <Panel datasetId="like-for-like"
          tall
          fixedHeight={false}
          title="Same quarter, same branches"
          unit="$ millions, Beacon perimeter only"
          takeaway="This is the cleanest read on the core asset. Sales are down every quarter; EBITDA fell faster than sales where both sides are disclosed."
          note={BEACON_LIKE_FOR_LIKE.map((r) => `${r.period}: ${r.note}`).join(' ')}
        >
          <DataTable rows={BEACON_LIKE_FOR_LIKE} columns={LFL_COLUMNS} rowKey={(r) => r.period} dense />
        </Panel>
      </div>

      <div className="grid min-w-0 grid-cols-1 gap-3 px-4 pb-4 lg:grid-cols-2">
        <Panel datasetId="qxo-quarters"
          tall
          fixedHeight={false}
          title="Quarterly P&L as reported"
          unit="$ millions; adjusted figures are the company's non-GAAP"
          takeaway="Five QXO quarters. Adjusted EBITDA has not yet cleared the Q3 25 level even with Kodiak added. Q1 26 SG&A rose $56M on $464M less revenue."
          note={QXO_QUARTERS.filter((q) => q.note).map((q) => `${q.period}: ${q.note}`).join(' ')}
        >
          <DataTable rows={QXO_QUARTERS} columns={QUARTER_COLUMNS} rowKey={(r) => r.period} dense highlightRow={(r) => r.period === LATEST.period} />
        </Panel>

        <Panel datasetId="beacon-standalone"
          tall
          fixedHeight={false}
          title="Beacon standalone, for reference"
          unit="$ millions, as Beacon filed them"
          takeaway="Beacon's own 2024: $9.76B of sales, $930M adjusted EBITDA, 25.7% gross margin, and a record Q4. That is the bar."
          note="Beacon did not file standalone Q1 25 results before the tender closed on 29 Apr 2025; the stub row is the predecessor period QXO filed."
        >
          <DataTable rows={BEACON_QUARTERS} columns={QUARTER_COLUMNS.filter((c) => !['sga', 'eps', 'cfo'].includes(c.key))} rowKey={(r) => r.period} dense highlightRow={(r) => r.period === 'FY 24'} />
        </Panel>
      </div>

      <Section title="Product lines and working capital">
        Residential roofing carries the decline: −3.8% in Q3 25, −13.9% in Q1 26, and −4.7% over two years in Q2 26.
        Complementary (waterproofing, siding, etc.) is the only line that has grown on a two-year basis, and Kodiak now
        sits inside it.
      </Section>

      <div className="grid min-w-0 grid-cols-1 gap-3 px-4 pb-4 lg:grid-cols-2">
        <Panel datasetId="product-lines"
          tall
          title="Product-line sales by quarter"
          unit="$ millions; Beacon 2024 and Q1 25, then QXO"
          takeaway={`Residential: ${signedPct(PRODUCT_LINE_YOY[0].q3)} (Q3 YoY), ${signedPct(PRODUCT_LINE_YOY[0].q1)} (Q1 YoY), ${signedPct(PRODUCT_LINE_YOY[0].q2TwoYear)} (Q2 vs Q2 24). Non-res: ${signedPct(PRODUCT_LINE_YOY[1].q3)}, ${signedPct(PRODUCT_LINE_YOY[1].q1)}, ${signedPct(PRODUCT_LINE_YOY[1].q2TwoYear)}. Complementary ex-Kodiak: ${signedPct(PRODUCT_LINE_YOY[2].q3)}, ${signedPct(PRODUCT_LINE_YOY[2].q1)}, ${signedPct(PRODUCT_LINE_YOY[2].q2TwoYear)}.`}
          note="Q2 25 and Q4 25 product lines were not disclosed comparably. Q2 26 complementary strips $595M Kodiak. Q2 26 vs Q2 24 is a two-year change because Q2 25 was a partial quarter."
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={PRODUCT_LINES} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#1e4976" vertical={false} />
              <XAxis dataKey="period" tick={{ fill: '#93c5fd', fontSize: 10 }} interval={0} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v: number) => `${(v / 1000).toFixed(1)}B`} />
              <Tooltip content={<ChartTooltip formatValue={(v) => fmtMoneyM(v)} />} />
              <Bar dataKey="residential" name="Residential roofing" fill="#38bdf8" radius={[2, 2, 0, 0]} />
              <Bar dataKey="nonResidential" name="Non-residential roofing" fill="#fbbf24" radius={[2, 2, 0, 0]} />
              <Bar dataKey="complementary" name="Complementary (ex-Kodiak)" fill="#86efac" radius={[2, 2, 0, 0]} />
              <Legend wrapperStyle={LEGEND} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel
          datasetId="product-lines"
          overviewOnly
          tall
          fixedHeight={false}
          title="Product-line detail"
          unit="$ millions"
          takeaway="Residential mix has slipped from ~50% under Beacon to 48% in Q2 26 before Kodiak. Mix shift toward non-res and complementary is margin-dilutive at the gross line."
        >
          <DataTable rows={PRODUCT_LINES} columns={PRODUCT_COLUMNS} rowKey={(r) => r.period} dense highlightRow={(r) => r.perimeter === 'QXO reported'} />
        </Panel>
      </div>

      <div className="grid min-w-0 grid-cols-1 gap-3 px-4 pb-4 lg:grid-cols-2">
        <Panel datasetId="working-capital"
          tall
          fixedHeight={false}
          title="Working capital, consolidated"
          unit="$ millions, balance-sheet dates"
          takeaway={`Net trade working capital went from ${fmtMoneyM(WC_ROWS[1].netTradeWc)} at year-end to ${fmtMoneyM(WC_ROWS[3].netTradeWc)} at Jun 30 (Kodiak added). Vendor rebates receivable is now ${fmtPct(WC_ROWS[3].rebatesToInventoryPct, 0)} of inventory — a large, judgment-heavy asset that flows through gross margin.`}
          note="Jun 30 26 includes Kodiak, not TopBuild. Prepaids at Jun 30 26 include ~$3.1B of TopBuild escrow proceeds and are excluded here. The rebates line is the one to watch if gross margin is being managed."
        >
          <DataTable rows={WC_ROWS} columns={WC_COLUMNS} rowKey={(r) => r.date} dense />
        </Panel>

        <Panel datasetId="fcf-vs-pref"
          tall
          title="Free cash flow vs preferred dividends paid"
          unit="$ millions per quarter"
          takeaway={`Trailing four quarters: ${fmtMoneyM(TTM_FCF)} of free cash flow against ${fmtMoneyM(TTM_PREF)} of cash preferred dividends. Series C adds ~$36M a quarter from Q3 26; TopBuild adds its cash flow and the new debt adds its interest.`}
          note="FCF = operating cash flow less capex, from the YTD cash-flow statements. Preferred dividends from the financing section. Q2 26 preferred jumps because Series C started accruing."
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={FCF_VS_PREF} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#1e4976" vertical={false} />
              <XAxis dataKey="period" tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip formatValue={(v) => fmtMoneyM(v)} />} />
              <ReferenceLine y={0} stroke="#64748b" />
              <Bar dataKey="fcf" name="Free cash flow" fill="#38bdf8" radius={[3, 3, 0, 0]} />
              <Bar dataKey="preferred" name="Preferred dividends paid" fill="#fb7185" radius={[0, 0, 3, 3]} />
              <Legend wrapperStyle={LEGEND} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <div className="grid gap-3 px-4 pb-4 lg:grid-cols-2">
        <NoteList
          datasetId="proof-points"
          title="Management claims you can check"
          subtitle="Said on the record; status against the filings so far."
          items={MANAGEMENT_PROOF_POINTS.map((p) => ({
            head: p.claim,
            body: p.checkAgainst,
            foot: p.saidWhen,
            badge: STATUS_BADGE[p.status],
          }))}
        />
        <NoteList
          title="Where each number comes from"
          subtitle="Every row on this board maps to one of these documents."
          items={FILINGS_SOURCES.map((s, i) => ({ head: `Source ${i + 1}`, body: s }))}
        />
      </div>
    </BoardShell>
  );
}
